import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  CATEGORY_ORDER,
  CATEGORY_PALETTE,
  CATEGORY_FALLBACK,
  categoryRank,
} from './lib/categoryPalette.js';
import { useEditionDraft } from './lib/useEditionDraft.js';
import { useArticleFeedback } from './lib/useArticleFeedback.js';
import { useBriefPulse } from './lib/useBriefPulse.js';
import {
  onAiBudgetExhausted,
  getCurrentBudgetState,
  clearBudgetState,
} from './lib/aiClient.js';
import PipelineDebrief from './PipelineDebrief.jsx';
import InlineArticle from './InlineArticle.jsx';
import BriefPulseCard from './BriefPulse.jsx';
import BriefFooter from './BriefFooter.jsx';
import SidebarShortcuts from './sidebar/SidebarShortcuts.jsx';
import ProgressPanel from './sidebar/ProgressPanel.jsx';
import BenchModal from './modals/BenchModal.jsx';
import ArchiveModal from './modals/ArchiveModal.jsx';
import TopicsModal from './modals/TopicsModal.jsx';
import AddSourceModal from './modals/AddSourceModal.jsx';
import FinalCheck from './FinalCheck.jsx';
import ImageAssignment from './ImageAssignment.jsx';
import InlineEmailPreview from './InlineEmailPreview.jsx';
import BriefComplete from './BriefComplete.jsx';
import FeedbackButton from './FeedbackButton.jsx';
import { api } from '../../../lib/api.js';

// Top-level orchestrator for the editor dashboard ("Wingman").
// Phase 2: PipelineDebrief → editing surface with inline copy editing and
// debounced autosave. Feedback flow + AI tools + final-check land in later phases.
export default function Editor() {
  const { date } = useParams();
  const draft = useEditionDraft(date);
  const feedback = useArticleFeedback(draft.date);
  const pulse = useBriefPulse(draft.date);
  const [view, setView] = useState('debrief'); // 'debrief' | 'edit' | 'final' | 'images' | 'preview' | 'complete'
  const [budget, setBudget] = useState(getCurrentBudgetState());
  const [modal, setModal] = useState(null); // 'bench' | 'archive' | 'topics' | 'add' | null
  // Response from POST /editions/:date/publish — { mode, beehiivPostUrl, ... }.
  // Passed into BriefComplete so the "Brief sent" / "Brief scheduled" copy is
  // accurate. Cleared whenever we leave the 'complete' view.
  const [publishResult, setPublishResult] = useState(null);

  // Surface a single budget-exhausted modal at the orchestrator level so
  // every AI tool surfaces the same message and no nested toast is needed.
  useEffect(() => onAiBudgetExhausted(setBudget), []);

  // Sort: category first (per CATEGORY_ORDER), then the existing `order` field
  // as the in-category tiebreaker. The `order` field is still the source of
  // truth for in-category position so the up/down arrows continue to work via
  // swapping order values between same-category neighbors.
  const sortedArticles = useMemo(() => {
    if (!draft.edition?.articles) return [];
    return [...draft.edition.articles].sort((a, b) => {
      const catDelta = categoryRank(a.category) - categoryRank(b.category);
      if (catDelta !== 0) return catDelta;
      return (a.order || 0) - (b.order || 0);
    });
  }, [draft.edition]);

  // Group sorted articles by category, preserving CATEGORY_ORDER and skipping
  // empty groups. Unknown categories (if the pipeline ever emits one) get
  // appended at the end so nothing is silently hidden.
  const groupedArticles = useMemo(() => {
    const groups = new Map();
    for (const c of CATEGORY_ORDER) groups.set(c, []);
    for (const a of sortedArticles) {
      const key = a.category || 'Uncategorized';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(a);
    }
    return Array.from(groups.entries()).filter(([, list]) => list.length > 0);
  }, [sortedArticles]);

  // Set of category names that are currently *collapsed*. Default = empty =
  // everything expanded, matching the editor's prior behavior on first load.
  const [collapsedCats, setCollapsedCats] = useState(() => new Set());
  const toggleCategory = (category) =>
    setCollapsedCats((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  const anyExpanded = groupedArticles.some(([c]) => !collapsedCats.has(c));
  const toggleAll = () =>
    setCollapsedCats(
      anyExpanded ? new Set(groupedArticles.map(([c]) => c)) : new Set()
    );

  // When a sidebar entry is clicked we may need to expand its category before
  // we can scroll to it. We record the pending scroll target in state and let
  // a useEffect run *after* React commits the expansion, so the DOM node is
  // guaranteed to exist before we call scrollIntoView.
  const [scrollTarget, setScrollTarget] = useState(null);
  useEffect(() => {
    if (!scrollTarget) return;
    const el = document.getElementById(`article-${scrollTarget}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setScrollTarget(null);
  }, [scrollTarget, collapsedCats]);

  const handleArticleClick = (article) => {
    if (!article) return;
    if (collapsedCats.has(article.category)) {
      setCollapsedCats((prev) => {
        const next = new Set(prev);
        next.delete(article.category);
        return next;
      });
    }
    setScrollTarget(article._id);
  };

  // ── Loading / error / empty states ────────────────────────────────────────

  if (draft.loading) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <p className="text-hxd-muted text-[13px]">Loading edition…</p>
      </div>
    );
  }

  if (draft.loadError) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <h1 className="hxd-page-title mb-4">Newsletter Editor</h1>
        <div className="hxd-card">
          <p className="text-hxd-err text-[13px] mb-3">Failed to load: {draft.loadError}</p>
          <button className="hxd-btn hxd-btn-ghost hxd-btn-sm" onClick={draft.reload}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (draft.notFound || !draft.edition) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <h1 className="hxd-page-title mb-4">Newsletter Editor</h1>
        <div className="hxd-card">
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-muted mb-2">
            {draft.date}
          </div>
          <p className="text-hxd-muted text-[13px]">
            No edition found for this day. Check the pipeline run.
          </p>
        </div>
      </div>
    );
  }

  // ── Debrief screen ───────────────────────────────────────────────────────

  if (view === 'debrief') {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <PipelineDebrief
          edition={draft.edition}
          onOpenBrief={() => setView('edit')}
        />
        <FeedbackButton isoDate={draft.date} view="debrief" />
      </div>
    );
  }

  // ── Final Check / Preview / Complete (Phase 7) ────────────────────────────

  if (view === 'final') {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <FinalCheck
          isoDate={draft.date}
          edition={draft.edition}
          onIntroChange={draft.updateIntroSummary}
          onIntroRedThreadChange={draft.updateIntroRedThread}
          onBack={() => setView('edit')}
          onPreview={() => setView('images')}
        />
        <FeedbackButton isoDate={draft.date} view="final" />
        {budget && <AiBudgetModal budget={budget} onDismiss={() => clearBudgetState()} />}
      </div>
    );
  }

  if (view === 'images') {
    // Move semantics: assigning an image clears any existing owner so each
    // pipeline image lives in at most one slot (article OR hero). Field-level
    // updates land in useEditionDraft's debounced pending queue and flush to
    // /:date PUT.
    const clearArticleImage = (articleId) => {
      draft.updateArticleField(articleId, 'imageUrl', '');
      draft.updateArticleField(articleId, 'imageAlt', '');
      draft.updateArticleField(articleId, 'imageAttributionHtml', '');
    };
    const clearHeroIfHolding = (url) => {
      if (draft.edition?.heroImage?.url === url) {
        draft.updateEditionField('heroImage', null);
      }
    };
    const handleAssign = (articleId, image) => {
      for (const a of draft.edition?.articles || []) {
        if (
          a.imageUrl === image.url &&
          String(a._id) !== String(articleId)
        ) {
          clearArticleImage(a._id);
        }
      }
      clearHeroIfHolding(image.url);
      draft.updateArticleField(articleId, 'imageUrl', image.url || '');
      draft.updateArticleField(articleId, 'imageAlt', image.alt || '');
      draft.updateArticleField(
        articleId,
        'imageAttributionHtml',
        image.attribution || ''
      );
    };
    const handleClear = (articleId) => clearArticleImage(articleId);
    const handleAssignHero = (image) => {
      // Clear any article currently using this URL — hero gets exclusive hold.
      for (const a of draft.edition?.articles || []) {
        if (a.imageUrl === image.url) clearArticleImage(a._id);
      }
      draft.updateEditionField('heroImage', {
        url: image.url || '',
        alt: image.alt || '',
        attributionHtml: image.attribution || '',
      });
    };
    const handleClearHero = () => {
      draft.updateEditionField('heroImage', null);
    };
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <ImageAssignment
          edition={draft.edition}
          onAssign={handleAssign}
          onClear={handleClear}
          onAssignHero={handleAssignHero}
          onClearHero={handleClearHero}
          onBack={() => setView('final')}
          onNext={async () => {
            // Flush any pending image patches so the iframe renders with the
            // editor's final assignments rather than an in-flight state.
            await draft.saveNow();
            setView('preview');
          }}
        />
        <FeedbackButton isoDate={draft.date} view="images" />
        {budget && <AiBudgetModal budget={budget} onDismiss={() => clearBudgetState()} />}
      </div>
    );
  }

  if (view === 'preview') {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <InlineEmailPreview
          isoDate={draft.date}
          edition={draft.edition}
          onBack={() => setView('images')}
          onChangeEditionField={draft.updateEditionField}
          onSend={async ({ mode = 'schedule', scheduledFor, force } = {}) => {
            // Real Beehiiv send. Saves any pending edits first, then calls the
            // publish endpoint with the requested mode. The UI only triggers
            // 'schedule' now — Beehiiv schedules for scheduledFor; status='scheduled'.
            //
            // `force` overrides the server's re-publish guard. The schedule modal
            // sets it after the editor confirms re-scheduling an edition that was
            // already pushed/sent to Beehiiv (a 409 on the unforced attempt).
            //
            // Errors bubble up so ScheduleSendModal can surface them instead of
            // falsely showing the "Brief sent" screen.
            await draft.saveNow();
            const body = { mode };
            if (mode === 'schedule') body.scheduledFor = scheduledFor;
            if (force) body.force = true;
            const res = await api.post(`/editions/${draft.date}/publish`, body);
            setPublishResult(res);
            setView('complete');
          }}
        />
        <FeedbackButton isoDate={draft.date} view="preview" />
        {budget && <AiBudgetModal budget={budget} onDismiss={() => clearBudgetState()} />}
      </div>
    );
  }

  if (view === 'complete') {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <BriefComplete
          isoDate={draft.date}
          edition={draft.edition}
          pulse={pulse.pulse}
          onPulseChange={pulse.replace}
          feedback={feedback}
          publishResult={publishResult}
          onBackToEditor={() => {
            setPublishResult(null);
            setView('debrief');
          }}
        />
        <FeedbackButton isoDate={draft.date} view="complete" />
        {budget && <AiBudgetModal budget={budget} onDismiss={() => clearBudgetState()} />}
      </div>
    );
  }

  // ── Editing surface ──────────────────────────────────────────────────────

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
            Wingman · {draft.edition.isoDate}
          </div>
          <h1 className="hxd-page-title">Today's brief</h1>
        </div>
        <button
          type="button"
          onClick={() => setView('debrief')}
          className="hxd-btn hxd-btn-ghost hxd-btn-sm"
        >
          ← Debrief
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <div className="min-w-0">
          <div className="flex items-end justify-between mb-4 pb-2.5 border-b-2 border-hxd-orange">
            <div className="font-display text-[11px] font-bold tracking-display-md uppercase">
              Articles{' '}
              <span className="font-sans font-normal normal-case tracking-normal text-hxd-muted text-[11px]">
                ({sortedArticles.length})
              </span>
            </div>
            {groupedArticles.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="font-display text-[10px] font-bold tracking-display-sm uppercase text-hxd-muted hover:text-hxd-orange transition-colors"
              >
                {anyExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </div>

          {sortedArticles.length === 0 ? (
            <div className="hxd-card">
              <p className="text-hxd-muted text-[13px]">No articles on this edition yet.</p>
            </div>
          ) : (
            groupedArticles.map(([category, group]) => {
              const palette = CATEGORY_PALETTE[category] || CATEGORY_FALLBACK;
              const isCollapsed = collapsedCats.has(category);
              const sectionId = `cat-section-${category.toLowerCase().replace(/\s+/g, '-')}`;
              return (
                <section
                  key={category}
                  className="mb-5 rounded-[4px] overflow-hidden"
                  style={{
                    backgroundColor: palette.track,
                    borderLeft: `4px solid ${palette.bar}`,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleCategory(category)}
                    aria-expanded={!isCollapsed}
                    aria-controls={sectionId}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-black/[0.04] transition-colors"
                  >
                    <span
                      className="font-display text-[12px] font-bold tracking-display-md uppercase"
                      style={{ color: palette.ink }}
                    >
                      {category}
                    </span>
                    <span className="font-mono text-[11px] text-hxd-muted">
                      {group.length} {group.length === 1 ? 'article' : 'articles'}
                    </span>
                    <span
                      aria-hidden="true"
                      className="ml-auto font-mono text-[14px] text-hxd-muted leading-none transition-transform duration-150"
                      style={{
                        display: 'inline-block',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      }}
                    >
                      ▾
                    </span>
                  </button>
                  {!isCollapsed && (
                    <div id={sectionId} className="px-3 pt-1">
                      {group.map((article) => {
                        // Up/down arrows only swap with the previous/next article of the
                        // same category. Cross-category swaps would re-sort to the same
                        // position and produce a confusing no-op, so we disable those.
                        const i = sortedArticles.indexOf(article);
                        const prevSameCat =
                          sortedArticles
                            .slice(0, i)
                            .reverse()
                            .find((a) => a.category === article.category) || null;
                        const nextSameCat =
                          sortedArticles
                            .slice(i + 1)
                            .find((a) => a.category === article.category) || null;
                        return (
              <InlineArticle
                key={String(article._id)}
                article={article}
                index={i}
                totalCount={sortedArticles.length}
                isoDate={draft.date}
                onChange={(field, value) =>
                  draft.updateArticleField(article._id, field, value)
                }
                onAcceptRewrite={({ draft: newSummary, signals }) => {
                  draft.updateArticleField(article._id, 'summary', newSummary);
                  feedback.update(article._id, {
                    toolUsed: 'rewrite',
                    toolResult: {
                      original: article.summary || '',
                      draft: newSummary,
                      final: newSummary,
                      ...signals,
                    },
                  });
                }}
                onVerifyResult={(result) => {
                  feedback.update(article._id, {
                    toolUsed: 'verify',
                    toolResult: {
                      status: result.status,
                      confidence: result.confidence,
                      note: result.note,
                      sources: result.sources,
                    },
                  });
                }}
                onMoveUp={
                  prevSameCat
                    ? () =>
                        draft.reorderArticles([
                          { articleId: article._id, order: prevSameCat.order },
                          { articleId: prevSameCat._id, order: article.order },
                        ])
                    : null
                }
                onMoveDown={
                  nextSameCat
                    ? () =>
                        draft.reorderArticles([
                          { articleId: article._id, order: nextSameCat.order },
                          { articleId: nextSameCat._id, order: article.order },
                        ])
                    : null
                }
                onCut={async ({ cutReason = '', blackoutKeywords = [] } = {}) => {
                  // Both fields are optional. We store the free-text reason on
                  // branchAnswer (so it surfaces in BriefComplete's "Cut reasons"
                  // panel and in the AI day-diagnosis prompt) and stash the
                  // joined blackout keywords on followupAnswer as a secondary
                  // signal — even though the keywords themselves are also
                  // persisted to the KeywordBlackout collection by /cut.
                  await feedback.update(article._id, {
                    q1Answer: 'bad_stopped',
                    q1Label: 'Cut',
                    branch: 'bad_stopped',
                    branchAnswer: cutReason,
                    branchLabel: 'Cut reason',
                    followupAnswer: blackoutKeywords.join(', '),
                    followupLabel: 'Blackout keywords',
                  });
                  await draft.cutArticle(article._id, { blackoutKeywords });
                }}
                onRemove={() => {
                  const ok = window.confirm(
                    `Remove "${article.headline || 'this article'}"?`
                  );
                  if (ok) draft.deleteArticle(article._id);
                }}
              />
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })
          )}

          <BriefPulseCard
            pulse={pulse.pulse}
            onChange={pulse.update}
            onContinue={async () => {
              await draft.saveNow();
              setView('final');
            }}
            continueDisabled={draft.saving}
          />
        </div>

        <aside className="hidden lg:flex flex-col gap-5 sticky top-24 self-start">
          <SidebarShortcuts
            enabled={{ bench: true, archive: true, topics: true, add: true }}
            onSelect={(key) => setModal(key)}
          />
          <ProgressPanel
            articles={sortedArticles}
            onArticleClick={handleArticleClick}
          />
        </aside>
      </div>

      <BriefFooter
        articleCount={sortedArticles.length}
        saving={draft.saving}
        savedAt={draft.savedAt}
        saveError={draft.saveError}
        onSaveNow={draft.saveNow}
      />

      {modal === 'bench' && (
        <BenchModal
          isoDate={draft.date}
          onClose={() => setModal(null)}
          onEditionUpdate={(next) => draft.replaceEdition(next)}
        />
      )}
      {modal === 'archive' && <ArchiveModal onClose={() => setModal(null)} />}
      {modal === 'topics' && <TopicsModal onClose={() => setModal(null)} />}
      {modal === 'add' && (
        <AddSourceModal
          isoDate={draft.date}
          onClose={() => setModal(null)}
          onInsert={(payload) => draft.insertArticle(payload)}
        />
      )}

      <FeedbackButton isoDate={draft.date} view="edit" />

      {budget && <AiBudgetModal budget={budget} onDismiss={() => clearBudgetState()} />}
    </div>
  );
}

function AiBudgetModal({ budget, onDismiss }) {
  const resetsAt = budget?.resetsAt ? new Date(budget.resetsAt) : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white max-w-md w-full rounded p-6 border border-hxd-border">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-err mb-2">
          AI budget hit
        </div>
        <h2 className="font-display text-[18px] font-bold mb-3">
          Rewrite, Verify, and Overview are disabled
        </h2>
        <p className="text-[13px] text-hxd-ink mb-2">
          Today's AI spend reached the configured daily cap (${budget?.budget?.toFixed(2)}
          used, limit ${budget?.budget?.toFixed(2)}).
        </p>
        {resetsAt && (
          <p className="text-[12px] text-hxd-muted mb-4">
            Cap resets {resetsAt.toLocaleString()}.
          </p>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="hxd-btn hxd-btn-primary hxd-btn-sm"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
